/**
 * Armour: what is worn, and what it takes off a blow.
 *
 * Four pieces, because that is how a body divides - head, trunk, arms, feet -
 * and because a Cossack's kit divided the same way: шишак, кольчуга, наручі,
 * чоботи. Not "helmet, chestplate, leggings, boots" under Ukrainian names;
 * наручі are bracers and cover the arms, which leggings do not.
 *
 * **A point is five per cent off, and nothing else.** No enchantments, no
 * toughness, no per-damage-type tables. The whole set is eight points, so a
 * fully armoured player takes 60% of what an unarmoured one takes - a real
 * difference in a raid, and nowhere near immunity. The orcs get heavier every
 * wave; armour has to be worth wearing without ending the fight.
 *
 * There is no durability, because no tool in this game has any either. When
 * wear arrives it should arrive for everything at once, or the sabre that
 * lasts forever will make the кольчуга that does not look like a bug.
 *
 * Pure rules over numbers - no items, no inventory, no DOM.
 */

/** Where a piece is worn. */
/**
 * Where a piece is worn. The shield is worn too - on the off arm - which is
 * what makes a slot the right place for it: "додай слот для щита, щоб гравець
 * міг взяти його в руку". It turns no blow aside by itself.
 */
export type ArmourSlot = "head" | "body" | "arms" | "feet" | "shield";

import type { Phrase } from "../text/lang.js";

/** In the order they are worn and drawn, top to bottom. */
export const ARMOUR_SLOTS: readonly ArmourSlot[] = ["head", "body", "arms", "feet", "shield"];

export const ARMOUR_SLOT_LABELS: Readonly<Record<ArmourSlot, Phrase>> = {
  head: { uk: "голова", en: "head" },
  body: { uk: "тулуб", en: "body" },
  arms: { uk: "руки", en: "arms" },
  feet: { uk: "ноги", en: "feet" },
  shield: { uk: "щит", en: "shield" },
};

/** What one point of armour takes off a blow. */
export const POINT_RELIEF = 0.05;

/**
 * The most armour can ever take off, as a fraction.
 *
 * The full iron set comes to eight points and so never reaches this. It is
 * here for the tiers that will follow: without a ceiling, enough plates would
 * eventually make a player unkillable, and a game that cannot be lost is not
 * being defended.
 */
export const MAX_RELIEF = 0.8;

/** What a piece of armour is worth. */
export interface ArmourDef {
  readonly slot: ArmourSlot;
  /** Points, each worth POINT_RELIEF. */
  readonly points: number;
}

/** What a blow of `damage` costs a body wearing `points` of armour. */
export function damageAfterArmour(damage: number, points: number): number {
  if (damage <= 0) return 0;
  const relief = Math.min(MAX_RELIEF, Math.max(0, points) * POINT_RELIEF);
  return damage * (1 - relief);
}

/**
 * Whether a piece belongs in a given slot.
 *
 * A rule rather than a UI check, so that "чоботи do not go on your head" is
 * something the game knows and a test can ask about, rather than something
 * that happens to be true of the current markup.
 */
export function fitsSlot(piece: ArmourDef | undefined, slot: ArmourSlot): boolean {
  return piece !== undefined && piece.slot === slot;
}

/** The share of a blow armour turns aside, for showing the player. */
export function reliefPercent(points: number): number {
  return Math.round(Math.min(MAX_RELIEF, Math.max(0, points) * POINT_RELIEF) * 100);
}
